import React, { useContext, useState, useEffect, useMemo, useRef } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { Input, Select, Form as AntdForm, Modal, Spin } from 'antd'
import { MaskedInput } from 'antd-mask-input'
import { Checkbox } from '../../components'
import { getInn, getFirstName, getLastName, getToken } from '../../shared/app'
import style from './Form.module.scss'
import { UserContext, UserContextType, User } from '../../shared/contexts'
import {
  getCachedFormSlots,
  invalidateDoctorSlotsCacheForToken,
  invalidateFormSlotsCacheForToken,
  setCachedFormSlots,
} from '../../shared/slotsCache'
import { formatPrice, formatSlotDate, getMondayDateString } from '../../shared/dateFormat'
import { isRepeatService } from '../../shared/serviceFilters'
// import { FREE_SERVICES, PAID_SERVICES } from '../../widgets/Service/mock'
// import { DOCTORS } from '../../widgets/Doctor/mock'
import { Button } from '../../components/button/button'
import { BackButton } from '../../components/BackButton'
import api from '../../app/api'
import { REQUESTS_API_URL, REQUESTS_API_AUTH } from '../../app/config'
import { useTranslation } from '../../shared/hooks/useTranslation'

// const DATES = [
//   { value: '1', label: '20 февраля 2025, 11:00' },
//   { value: '2', label: '20 февраля 2025, 11:30' },
//   { value: '3', label: '20 февраля 2025, 12:00' },
//   { value: '4', label: '21 февраля 2025, 11:00' },
//   { value: '5', label: '21 февраля 2025, 11:30' },
//   { value: '6', label: '21 февраля 2025, 13:00' },
// ]

interface FormValues {
  date: string
  fullName: string
  phone: string
}

interface ServiceOption {
  usluga_name: string
  podrazdelenie_name: string
  price: number
  doctor_ids: string[]
}

interface DoctorOption {
  doktor_id: string
  doktor_name: string
  podrazdelenie_name: string
  price: number
}

// Функция для парсинга даты из строки "20 февраля 2025, 11:00"
function parseAppointmentDate(dateTimeStr: string): Date {
  const months = {
    'января': 0, 'февраля': 1, 'марта': 2, 'апреля': 3, 'мая': 4, 'июня': 5,
    'июля': 6, 'августа': 7, 'сентября': 8, 'октября': 9, 'ноября': 10, 'декабря': 11
  };
  
  // Простой парсинг через split (работает надежнее)
  const parts = dateTimeStr.split(' ');
  
  if (parts.length >= 4) {
    const day = parts[0];
    const monthName = parts[1];
    const yearTime = parts[2];
    const time = parts[3];
    
    // Извлекаем год из "2025,"
    const year = yearTime.replace(',', '');
    const [hour, minute] = time.split(':');
    
    const month = months[monthName as keyof typeof months];
    if (month !== undefined) {
      return new Date(parseInt(year), month, parseInt(day), parseInt(hour), parseInt(minute));
    }
  }
  
  // Если не удалось распарсить, возвращаем текущую дату
  return new Date();
}

export function Form() {
  const [form] = AntdForm.useForm()
  const fullNameValue = AntdForm.useWatch('fullName', form)
  const phoneValue = AntdForm.useWatch('phone', form)
  const { t, language } = useTranslation()
  const [agree1, setAgree1] = useState<boolean>(false)
  const [agree2, setAgree2] = useState<boolean>(false)
  const navigate = useNavigate()
  const { siteId } = useParams<{ siteId: string }>()
  // Реальный siteId публичной записи — это DR-код (например DR0355, UM0168) ЛИБО
  // числовой публичный ID из файла врачей (например 19191). Бэкенд сам резолвит
  // числовой ID в DR-код. Косметический слаг с ФИО (например /form/ivanov-ivan)
  // под шаблон не подходит и не запускает prefill — страница ведёт себя как
  // обычный /form (данные берутся из контекста).
  const bookingSiteId =
    siteId && /^([A-Za-z]{2,4}\d+|\d+)$/.test(siteId) ? siteId : undefined
  const { user, setUser } = useContext<UserContextType>(UserContext)
  const [siteBookingError, setSiteBookingError] = useState<string | null>(null)
  const [siteBookingLoading, setSiteBookingLoading] = useState(!!bookingSiteId)
  const [slots, setSlots] = useState<{ value: string, label: string }[]>([])
  const [selectedSlot, setSelectedSlot] = useState<{ value: string, label: string } | null>(null)
  const [isAlreadyBookedModalVisible, setIsAlreadyBookedModalVisible] = useState(false)
  const [services, setServices] = useState<ServiceOption[]>([])
  const [loadingServices, setLoadingServices] = useState(false)
  const [doctors, setDoctors] = useState<DoctorOption[]>([])
  const [loadingDoctors, setLoadingDoctors] = useState(false)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const doctorsRequestIdRef = useRef(0)
  const slotsRequestIdRef = useRef(0)

  // Предзаполняем ФИО при загрузке компонента
  useEffect(() => {
    const firstName = getFirstName();
    const lastName = getLastName();
    if (firstName && lastName) {
      form.setFieldValue('fullName', `${firstName} ${lastName}`);
    }
  }, [form]);

  const onChangeAgree1 = (checked: boolean) => setAgree1(checked)
  const onChangeAgree2 = (checked: boolean) => setAgree2(checked)

  const handleAlreadyBookedModalOk = () => {
    setIsAlreadyBookedModalVisible(false)
  }

  useEffect(() => {
    let cancelled = false
    setLoadingServices(true)
    api
      .get('/services/available', { params: { lang: language } })
      .then((res) => {
        if (cancelled) return
        const data = Array.isArray(res.data) ? res.data : []
        setServices(
          data
            .map((s: any) => ({
              usluga_name: String(s.usluga_name || '').trim(),
              podrazdelenie_name: String(s.podrazdelenie_name || ''),
              price: Number(s.price) || 0,
              doctor_ids: String(s.doctor_ids || '')
                .split(',')
                .map((id: string) => id.trim())
                .filter(Boolean),
            }))
            // Защита: отсекаем услуги с пустым названием — в дропдауне
            // они отображались бы пустым пунктом.
            .filter((s: ServiceOption) => s.usluga_name.length > 0)
            // Скрываем повторные приёмы — они не должны попадать в выбор
            // на этом шаге записи (на всех языках, см. isRepeatService).
            .filter((s: ServiceOption) => !isRepeatService(s.usluga_name)),
        )
      })
      .catch(() => {
        if (!cancelled) setServices([])
      })
      .finally(() => {
        if (!cancelled) setLoadingServices(false)
      })
    return () => {
      cancelled = true
    }
  }, [language])

  useEffect(() => {
    if (!user?.serviceId) {
      setDoctors([])
      return
    }
    const requestId = ++doctorsRequestIdRef.current
    setLoadingDoctors(true)
    api
      .get('/services/doctors', { params: { name: user.serviceId, lang: language } })
      .then((res) => {
        if (requestId !== doctorsRequestIdRef.current) return
        const data = Array.isArray(res.data) ? res.data : []
        const mapped: DoctorOption[] = data.map((d: any) => ({
          doktor_id: String(d.doktor_id || ''),
          doktor_name: String(d.doktor_name || ''),
          podrazdelenie_name: String(d.podrazdelenie_name || ''),
          price: Number(d.price) || 0,
        }))
        setDoctors(mapped)

        // Цена и подразделение врача зависят от услуги — после смены услуги
        // подтянем актуальные значения для уже выбранного врача.
        setUser((prev) => {
          if (!prev?.doctorId) return prev
          const current = mapped.find((d) => d.doktor_id === prev.doctorId)
          if (!current) return prev
          if (
            current.price === prev.doctorPrice &&
            current.podrazdelenie_name === prev.doctorType
          ) {
            return prev
          }
          return {
            ...prev,
            doctorPrice: current.price,
            doctorType: current.podrazdelenie_name,
          }
        })
      })
      .catch(() => {
        if (requestId === doctorsRequestIdRef.current) setDoctors([])
      })
      .finally(() => {
        if (requestId === doctorsRequestIdRef.current) setLoadingDoctors(false)
      })
  }, [user?.serviceId, language, setUser])

  useEffect(() => {
    async function fetchSlots() {
      if (!user?.doctorId || !user?.token) {
        setSlots([])
        setSelectedSlot(null)
        setLoadingSlots(false)
        return;
      }

      const requestId = ++slotsRequestIdRef.current

      const applyOptions = (options: { value: string, label: string }[]) => {
        if (requestId !== slotsRequestIdRef.current) return
        let finalOptions = options

        if (user?.date) {
          let preselected = finalOptions.find((s) => s.value === user.date)

          if (!preselected && user.selectedDateTime) {
            preselected = { value: user.date, label: user.selectedDateTime }
            finalOptions = [preselected, ...finalOptions]
          }

          if (preselected) {
            form.setFieldValue('date', preselected.value)
            setSelectedSlot(preselected)
          }
        } else {
          setSelectedSlot(null)
        }

        setSlots(finalOptions)
      }

      const cached = getCachedFormSlots(language, user.doctorId, user.token)
      if (cached) {
        setLoadingSlots(false)
        applyOptions(cached)
        return
      }

      setLoadingSlots(true)

      // hafta — параметр API УМЦ в формате DD/MM/YYYY, не зависит от UI-языка
      const haftaStr = getMondayDateString(0)
      const nextWeekStr = getMondayDateString(1)

      try {
        // Сначала пробуем текущую неделю
        let res = await api.get('/external/slots', {
          params: {
            token: user.token,
            doktorId: user.doctorId,
            hafta: haftaStr,
            lang: language,
          }
        });

        let data = res.data;
        let options: { value: string, label: string }[] = [];

        if (!(data && data.rndvList && data.rndvList.length > 0)) {
          // Пробуем следующую неделю
          res = await api.get('/external/slots', {
            params: {
              token: user.token,
              doktorId: user.doctorId,
              hafta: nextWeekStr,
              lang: language,
            }
          });
          data = res.data;
        }

        if (data && data.rndvList) {
          data.rndvList.forEach((day: any) => {
            if (Array.isArray(day.rndv)) {
              day.rndv.forEach((slot: any) => {
                // Backend уже отфильтровал слоты с kapali = "X",
                // поэтому просто проверяем наличие времени и даты
                if (slot.saat && slot.tarih) {
                  options.push({
                    value: String(slot.randevuId),
                    label: `${formatSlotDate(slot.tarih, language)}, ${slot.saat}`
                  });
                }
              });
            }
          });
        }

        if (requestId !== slotsRequestIdRef.current) return

        if (options.length > 0) {
          setCachedFormSlots(language, user.doctorId, user.token, options)
        }

        applyOptions(options)
      } catch (e: any) {
        console.error(
          `[form/slots] fetch failed doctorId=${user.doctorId}`,
          e?.response?.status,
          e?.response?.data || e?.message,
        )
        if (requestId === slotsRequestIdRef.current) setSlots([])
      } finally {
        if (requestId === slotsRequestIdRef.current) setLoadingSlots(false)
      }
    }
    fetchSlots();
  }, [user?.doctorId, user?.token, user?.date, user?.selectedDateTime, form, language]);

  useEffect(() => {
    if (!bookingSiteId) {
      setSiteBookingLoading(false)
      return
    }
    if (!getToken()) {
      return
    }
    let cancelled = false
    setSiteBookingLoading(true)
    setSiteBookingError(null)
    void (async () => {
      try {
        const { data } = await api.get(
          `/specialists/public/booking/${encodeURIComponent(bookingSiteId)}`,
        )
        if (cancelled) return
        setUser((prev) => {
          const base: User = { ...(prev || {}) }
          return {
            ...base,
            token: getToken() || base.token,
            serviceId: data.service_name as string,
            doctorId: String(data.doktor_id),
            doctorName: data.doctor_name as string,
            doctorType: data.doctor_type as string,
            doctorPrice: Number(data.doctor_price) || 0,
            date: undefined,
            selectedDateTime: undefined,
          }
        })
        if (!cancelled) setSiteBookingLoading(false)
      } catch (e: any) {
        if (cancelled) return
        setSiteBookingError(
          e?.response?.data?.error || t.formDoctorLoadError,
        )
        setSiteBookingLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bookingSiteId, setUser])

  const dashLabel = '—'

  const serviceOptionsGrouped = useMemo(() => {
    // Если выбран врач — показываем только услуги, которые он оказывает.
    // Без выбранного врача показываем все услуги.
    const doctorId = user?.doctorId
    const filteredServices = doctorId
      ? services.filter((s) => s.doctor_ids.includes(doctorId))
      : services

    const groups = new Map<string, ServiceOption[]>()
    filteredServices.forEach((s) => {
      const dept = s.podrazdelenie_name || dashLabel
      const arr = groups.get(dept) || []
      arr.push(s)
      groups.set(dept, arr)
    })
    const locale = language === 'kk' ? 'kk' : language === 'en' ? 'en' : 'ru'
    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0], locale))
      .map(([dept, items]) => ({
        label: dept,
        options: items
          .slice()
          .sort((a, b) => a.usluga_name.localeCompare(b.usluga_name, locale))
          .map((s) => ({
            value: s.usluga_name,
            label: `${s.usluga_name} — ${t.fromPrice} ${formatPrice(s.price, language)} ${t.tenge}`,
          })),
      }))
  }, [services, language, t.tenge, t.fromPrice, user?.doctorId])

  const doctorOptions = useMemo(
    () =>
      doctors.map((d) => ({
        value: d.doktor_id,
        label: d.doktor_name,
      })),
    [doctors],
  )

  if (bookingSiteId && !getToken()) {
    return (
      <Navigate
        to={`/?returnTo=${encodeURIComponent(`/form/${bookingSiteId}`)}`}
        replace
      />
    )
  }

  if (bookingSiteId && siteBookingError) {
    return (
      <div className={style.form}>
        <p style={{ marginBottom: 16 }}>{siteBookingError}</p>
        <Link to="/service">{t.goToServiceSelection}</Link>
      </div>
    )
  }

  if (bookingSiteId && siteBookingLoading) {
    return (
      <div
        className={style.form}
        style={{ textAlign: 'center', padding: 48 }}
      >
        <Spin size="large" />
      </div>
    )
  }

  if (!bookingSiteId && !getToken()) {
    return <Navigate to="/" replace />
  }

  const serviceName = user?.serviceId || ''
  const doctorId = user?.doctorId || ''
  const doctorType = user?.doctorType
  const doctorPrice = user?.doctorPrice

  const handleServiceChange = (newServiceName: string) => {
    setUser((prev) => ({
      ...(prev || {}),
      serviceId: newServiceName,
      // Врач не очищается: список услуг отфильтрован услугами текущего врача,
      // поэтому он остаётся валидным. doctorPrice/doctorType актуализируются
      // в useEffect, когда подтянется список врачей под новую услугу.
      date: undefined,
      selectedDateTime: undefined,
    }))
    form.setFieldValue('date', undefined)
    setSelectedSlot(null)
  }

  const handleDoctorChange = (newDoctorId: string) => {
    const chosen = doctors.find((d) => d.doktor_id === newDoctorId)
    if (!chosen) return
    setUser((prev) => ({
      ...(prev || {}),
      doctorId: chosen.doktor_id,
      doctorName: chosen.doktor_name,
      doctorType: chosen.podrazdelenie_name,
      doctorPrice: chosen.price,
      date: undefined,
      selectedDateTime: undefined,
    }))
    form.setFieldValue('date', undefined)
    setSelectedSlot(null)
  }

  const servicePlaceholder = t.formServicePlaceholder
  const doctorPlaceholder = t.formDoctorPlaceholder
  const timePlaceholder = t.formTimePlaceholder
  const noTimeText = t.formNoTimeAvailable

  const isFullNameFilled = typeof fullNameValue === 'string' && fullNameValue.trim().length > 0
  // Маска +0 (000) 000-00-00 → при полном вводе ровно 11 цифр.
  const isPhoneFilled = typeof phoneValue === 'string' && phoneValue.replace(/\D/g, '').length === 11

  const canSubmit =
    agree1 &&
    !!user?.serviceId &&
    !!user?.doctorId &&
    !!selectedSlot &&
    isFullNameFilled &&
    isPhoneFilled

  const onFinish = async (values: FormValues) => {
    try {
      if (!user) {
        alert(t.userDataError);
        return;
      }

      // Получаем данные услуги и доктора для API
      const serviceDataRes = await api.get('/services/data', {
        params: {
          serviceName: user.serviceId,
          doctorName: user.doctorName,
          lang: language
        }
      });

      const serviceData = serviceDataRes.data;
      
      // Подготавливаем данные для второго сервиса
      const secondaryServiceData = {
        request_date: new Date().toISOString(),
        iin: getInn() || '',
        client_full_name: values.fullName,
        client_phone: values.phone,
        appointment_date: selectedSlot?.label ? parseAppointmentDate(selectedSlot.label).toISOString() : new Date().toISOString(),
        doctor_full_name: user.doctorName || '',
        service_cost: user.doctorPrice || 0,
        department: serviceData.podrazdelenie_name || ''
      };

      // Basic Auth для стороннего сервиса берётся из env (REQUESTS_API_AUTH),
      // секрет больше не зашит в исходный код. Если значение не задано —
      // заголовок не отправляется (запрос не блокирует основную запись, см. allSettled).
      const requestsAuthHeader: Record<string, string> = REQUESTS_API_AUTH
        ? { 'Authorization': 'Basic ' + btoa(REQUESTS_API_AUTH) }
        : {};

      // Параллельные запросы: основной и дополнительный
      const [createAppointmentRes, secondaryServiceRes] = await Promise.allSettled([
        // Основной запрос на создание записи
        api.post('/external/set-randevu', null, {
          params: {
            token: user.token,
            randevuId: values.date,
            hastaneId: '1',
            birimId: serviceData.podrazdelenie_id,
            doktorId: serviceData.doktor_id,
            tetkik: serviceData.usluga_id
          }
        }),
        // Дополнительный запрос на сторонний сервис
        fetch(REQUESTS_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...requestsAuthHeader
          },
          body: JSON.stringify(secondaryServiceData)
        })
      ]);

      // Обрабатываем результат основного запроса
      if (createAppointmentRes.status === 'fulfilled') {
        const result = createAppointmentRes.value.data;
        
        if (result.success) {
          // После успешной записи слот занят — сбрасываем кэш,
          // чтобы при возврате на /form или /doctor показывались актуальные слоты.
          if (user.token) {
            invalidateFormSlotsCacheForToken(user.token)
            invalidateDoctorSlotsCacheForToken(user.token)
          }
          // Сохраняем данные формы в контекст
          setUser({
            ...user,
            date: values.date,
            selectedDateTime: selectedSlot?.label || '',
            fullName: values.fullName,
            phone: values.phone,
          })
          navigate('/schedule')
        } else {
          // Проверяем, не является ли ошибка сообщением о том, что пользователь уже записан
          const errorMessage = result.error || result.message || '';
          if (errorMessage.includes('Aynı birime randevunuz') || errorMessage.includes('Ayni birime randevunuz')) {
            setIsAlreadyBookedModalVisible(true);
          } else {
            // Показываем обычную ошибку
            alert(`${t.appointmentError}: ${errorMessage || t.unknownError}`)
          }
        }
      } else {
        // Ошибка основного запроса
        const error = createAppointmentRes.reason;
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '';
        if (errorMessage.includes('Aynı birime randevunuz') || errorMessage.includes('Ayni birime randevunuz')) {
          setIsAlreadyBookedModalVisible(true);
        } else {
          alert(`${t.appointmentError}: ${errorMessage || t.unknownError}`)
        }
      }

      // Логируем результат дополнительного запроса (не влияет на основной поток)
      if (secondaryServiceRes.status === 'fulfilled') {
        const response = secondaryServiceRes.value;
        
        if (response.ok) {
          console.log('✅ Дополнительный сервис: запрос успешно отправлен');
        } else {
          console.error('❌ HTTP ошибка дополнительного сервиса:', response.status, response.statusText);
        }
      } else {
        console.error('❌ Ошибка API дополнительного сервиса:', secondaryServiceRes.reason?.message);
      }

    } catch (error: any) {

      
      // Проверяем, не является ли ошибка сообщением о том, что пользователь уже записан
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '';
      if (errorMessage.includes('Aynı birime randevunuz') || errorMessage.includes('Ayni birime randevunuz')) {
        setIsAlreadyBookedModalVisible(true);
      } else {
        // Показываем обычную ошибку
        alert(`${t.appointmentError}: ${errorMessage || t.unknownError}`)
      }
    }
  }

  return (
    <AntdForm form={form} name="basic" onFinish={onFinish} autoComplete="off">
      <div className={style.form}>
        <div className={style.formHeader}>
          <BackButton className={style.back} />
        </div>
        <div className={style.fields}>
          <div className={style.field}>
            <span className={`${style.hint} ${style.required}`}>{t.selectedService}</span>
            <Select
              size="large"
              showSearch
              placeholder={servicePlaceholder}
              value={serviceName || undefined}
              loading={loadingServices}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={serviceOptionsGrouped}
              onChange={handleServiceChange}
            />
          </div>

          <div className={style.field}>
            <span className={`${style.hint} ${style.required}`}>{t.selectedDoctor}</span>
            <Select
              size="large"
              showSearch
              placeholder={doctorPlaceholder}
              value={doctorId || undefined}
              loading={loadingDoctors}
              disabled={!serviceName}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={doctorOptions}
              onChange={handleDoctorChange}
              notFoundContent={loadingDoctors ? <Spin size="small" /> : null}
            />
          </div>

          <div className={style.field}>
            <span className={style.infoLabel}>{t.doctorType}</span>
            <span className={style.doctorType}>{doctorType || dashLabel}</span>
          </div>

          <div className={style.field}>
            <span className={style.infoLabel}>{t.experience}</span>
            <span className={style.doctorExperience}>
              {doctorPrice ? `${formatPrice(doctorPrice, language)} ${t.tenge}` : dashLabel}
            </span>
          </div>

          <span className={`${style.hint} ${style.required}`}>{t.appointmentDateTime}</span>
          <AntdForm.Item name="date" rules={[{ required: true, message: t.requiredField }]} className={style.field}>
              <Select
                size="large"
                placeholder={timePlaceholder}
                options={slots}
                disabled={!doctorId}
                onChange={(value) => {
                  form.setFieldValue('date', value)
                  const slot = slots.find(s => s.value === value)
                  setSelectedSlot(slot || null)
                }}
                loading={loadingSlots}
                notFoundContent={
                  doctorId && !loadingSlots && slots.length === 0 ? (
                    <span style={{ color: 'var(--text-secondary)' }}>{noTimeText}</span>
                  ) : null
                }
              />
          </AntdForm.Item>

          <div className={style.field}>
            <span className={style.hint}>{t.yourIIN}</span>
            <Input size="large" placeholder={t.iinPlaceholder} value={getInn() || ''} disabled />
          </div>

          <span className={`${style.hint} ${style.required}`}>{t.fullName}</span>
          <AntdForm.Item name="fullName" rules={[{ required: true, message: t.requiredField }]}> 
              <Input size="large" placeholder={t.fullNamePlaceholder} />
          </AntdForm.Item>

          <span className={`${style.hint} ${style.required}`}>{t.phoneHint}</span>
          <AntdForm.Item name="phone" rules={[{ required: true, message: t.requiredField }]}> 
              <MaskedInput
                size="large"
                mask="+0 (000) 000-00-00"
                placeholder={t.phonePlaceholder}
                onChange={(e) => {
                  form.setFieldValue('phone', e.target.value)
                }}
              />
          </AntdForm.Item>
        </div>


        <div className={style.agreements}>
          <Checkbox
            callback={onChangeAgree1}
          >
            <p>
              {(() => {
                const parts = t.privacyAgreement.split(t.privacyPolicy);
                return parts.map((part, index, array) => (
                  <React.Fragment key={index}>
                    {part}
                    {index < array.length - 1 && (
                      <Link to={'/policy'} className={style.link}>
                        {t.privacyPolicy}
                      </Link>
                    )}
                  </React.Fragment>
                ));
              })()}
            </p>
          </Checkbox>
          <Checkbox
            callback={onChangeAgree2}
          >
            {t.smsAgreement}
          </Checkbox>
        </div>

        <AntdForm.Item>
          <Button htmlType="submit" disabled={!canSubmit} className={style.button}>
            {t.bookAppointment}
          </Button>
        </AntdForm.Item>
      </div>

      {/* Модальное окно для случая, когда пользователь уже записан */}
      <Modal
        title={t.alreadyBookedWarning}
        open={isAlreadyBookedModalVisible}
        onOk={handleAlreadyBookedModalOk}
        okText={t.okButton}
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
        className={style.modal}
      >
        <div className={style.modalContent}>
          <div className={style.modalText}>
            <p>{t.alreadyBookedTitle}</p>
            <p>{t.alreadyBookedMessage}</p>
          </div>
        </div>
      </Modal>
    </AntdForm>
  )
}